import { useEffect, useRef, useState } from 'react';
import { useEditor, type Broll } from '../../state/editorStore';
import { playbackClock } from '../../playback/clock';
import { brollBoundaryWindows } from '../../lib/transitions';

/**
 * Renders active B-roll clips on the stage: a 'cover' clip fills the frame, an
 * 'overlay' clip is a movable/resizable box (drag to move, corner to resize when
 * selected). Sits above the video, below the caption canvas. Video B-roll is
 * kept in sync with the transport. Preview-only for now (export is a follow-up).
 */
export function BrollLayer({ className }: { className?: string } = {}) {
  const brolls = useEditor((s) => s.brolls);
  const transitions = useEditor((s) => s.transitions);
  const selectedId = useEditor((s) => s.selectedBrollId);
  // A cover clip stays visible across a slide transition that reveals it, so the
  // reveal (base sliding off it, or it sliding off the base) shows real frames.
  const windowOf = (b: Broll): [number, number] => {
    if (b.mode !== 'cover') return [b.startMs, b.startMs + b.durationMs];
    const { visStartMs, visEndMs } = brollBoundaryWindows(b.startMs, b.startMs + b.durationMs, transitions);
    return [visStartMs, visEndMs];
  };
  const selectBroll = useEditor((s) => s.selectBroll);
  const updateBroll = useEditor((s) => s.updateBroll);
  const [nowMs, setNowMs] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  // Track time (throttled) to show/hide clips by their window.
  useEffect(() => {
    let last = 0;
    return playbackClock.subscribe((t) => {
      const n = performance.now();
      if (n - last < 80) return;
      last = n;
      setNowMs(t);
    });
  }, []);

  // Keep video B-roll in sync with the transport.
  useEffect(() => {
    return playbackClock.subscribe((t, playing) => {
      for (const b of brolls) {
        if (b.kind !== 'video') continue;
        const el = videoRefs.current.get(b.id);
        if (!el) continue;
        const [vs, ve] = windowOf(b);
        const active = t >= vs && t <= ve;
        if (!active) {
          if (!el.paused) el.pause();
          continue;
        }
        const target = (t - b.startMs) / 1000;
        if (Math.abs(el.currentTime - target) > 0.25) el.currentTime = Math.max(0, target);
        if (playing && el.paused) void el.play().catch(() => undefined);
        if (!playing && !el.paused) el.pause();
      }
    });
  }, [brolls, transitions]);

  const active = brolls.filter((b) => {
    const [vs, ve] = windowOf(b);
    return nowMs >= vs && nowMs <= ve;
  });

  const media = (b: Broll) =>
    b.kind === 'image' ? (
      <img src={b.url} alt="" style={{ objectFit: b.fit }} draggable={false} />
    ) : (
      <video
        ref={(el) => {
          if (el) videoRefs.current.set(b.id, el);
          else videoRefs.current.delete(b.id);
        }}
        src={b.url}
        style={{ objectFit: b.fit }}
        muted
        playsInline
      />
    );

  const startDrag = (b: Broll, e: React.PointerEvent, mode: 'move' | 'resize') => {
    e.stopPropagation();
    e.preventDefault();
    selectBroll(b.id);
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      if (mode === 'move') {
        const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
        const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
        updateBroll(b.id, { x, y });
      } else {
        // Resize from the centre: width = 2·distance to the centre X.
        const cx = rect.left + b.x * rect.width;
        const scale = Math.min(1, Math.max(0.1, (2 * Math.abs(ev.clientX - cx)) / rect.width));
        updateBroll(b.id, { scale });
      }
    };
    const onUp = (ev: PointerEvent) => {
      (e.target as HTMLElement).releasePointerCapture?.(ev.pointerId);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div className={className ? `broll-layer ${className}` : 'broll-layer'} ref={rootRef}>
      {active.map((b) =>
        b.mode === 'cover' ? (
          <div
            key={b.id}
            className={b.id === selectedId ? 'broll-cover sel' : 'broll-cover'}
            onPointerDown={() => selectBroll(b.id)}
          >
            {media(b)}
          </div>
        ) : (
          <div
            key={b.id}
            className={b.id === selectedId ? 'broll-overlay sel' : 'broll-overlay'}
            style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.scale * 100}%` }}
            onPointerDown={(e) => startDrag(b, e, 'move')}
          >
            {media(b)}
            {b.id === selectedId && (
              <span className="broll-handle" onPointerDown={(e) => startDrag(b, e, 'resize')} />
            )}
          </div>
        ),
      )}
    </div>
  );
}
